import React, {
 useEffect, useState, useRef, Fragment
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import SnmpDestinationForm, {
  Ref
} from 'routes/SnmpDestination/components/SnmpDestinationForm';
import * as snmpDestActions from 'data/snmp/snmp.actions';
import * as snmpDestSelectors from 'data/snmp/snmp.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { snmpDestTemplate } from 'routes/SnmpDestination/templates/snmpDestTemplate';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptTemplateOnLoad
} from 'routes/SnmpDestination/utils/snmpDestTemplate.adapter';
import { SnmpDestination } from 'data/snmp/snmp.types';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedSnmpDest: snmpDestSelectors.getSelectedSnmpDest(state),
  snmpDest: snmpDestSelectors.getSnmpDest(state),
  snmpDestTemplateDef: adaptTemplateVisibilityMode(snmpDestTemplate, 'view'),
  snmpUsers: snmpDestSelectors.getSnmpUsers(state)
});

const mapDispatch = {
  goToSmnpDestList: snmpDestActions.goToSmnpDestList,
  updateSnmpDest: snmpDestActions.updateSnmpDest,
  createSnmpDest: snmpDestActions.createSnmpDest,
  getSnmpDest: snmpDestActions.getSnmpDest,
  goToSnmpDestEdit: snmpDestActions.goToSnmpDestEdit,
  getSnmpUsers: snmpDestActions.getSnmpUsers
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpDestView = ({
  history,
  userId,
  userName,
  isAuditEnabled,
  deliveryFunctionId,
  goToSmnpDestList,
  snmpDestTemplateDef,
  updateSnmpDest,
  goToSnmpDestEdit,
  getSnmpDest,
  snmpDest,
  selectedSnmpDest,
  getSnmpUsers,
  snmpUsers
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SnmpDestination>>({});
  const [template, setTemplate] = useState(snmpDestTemplateDef);
  const { t } = useTranslation();

  const id = selectedSnmpDest?.id;

  useEffect(() => {
    if (id) {
      getSnmpDest(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getSnmpDest]);

  useEffect(() => {
    getSnmpUsers(deliveryFunctionId);
  }, [deliveryFunctionId, getSnmpUsers]);

  useEffect(() => {
    if (snmpDest == null || snmpDestTemplateDef == null) {
      return;
    }

    const clonedSnmpDest = cloneDeep(snmpDest);
    let clonedTemplate = cloneDeep(snmpDestTemplateDef);
    clonedTemplate.formId = Date.now().toString();

    clonedTemplate = adaptTemplateOptions(clonedTemplate, snmpUsers);
    clonedTemplate = adaptTemplateOnLoad(snmpDest, clonedTemplate);

    setTemplate(clonedTemplate);
    setFormData(clonedSnmpDest);
  }, [snmpDest, snmpDestTemplateDef, snmpUsers]);

  const handleClickEdit = () => goToSnmpDestEdit(history);

  const handleCancel = () => goToSmnpDestList(history);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SnmpDestination);

    setFormData({
      ...clonedFormData,
      [fieldName]: value
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <SnmpDestinationForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (snmpDest == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="SnmpDestView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('snmpDest.view.title'))}
          <div className="read-only">{renderForm()}</div>
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SnmpDestView));
